import moment, { type Moment } from "moment";

import type { DatePickerProps } from "@ps-design-system/components/DatePicker";

import { MAX_DATE_RATE_DAYS } from "./DateRateForm.consts";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

/** Groups sorted days into runs of consecutive days within the same month. */
const groupConsecutiveDaysByMonth = (days: Moment[]) =>
  days.reduce<Moment[][]>((runs, day) => {
    const lastRun = runs[runs.length - 1];
    const previousDay = lastRun?.[lastRun.length - 1];

    if (
      previousDay &&
      previousDay.isSame(day, "month") &&
      day.diff(previousDay, "days") === 1
    ) {
      lastRun.push(day);
    } else {
      runs.push([day]);
    }

    return runs;
  }, []);

/**
 * Formats picked dates compactly with the locale's own date range format:
 * consecutive days in the same month become one range ("Dec 1 – 3, Dec 10" in
 * en-US, "1–3 déc., 10 déc." in fr-FR). The year only shows when the dates
 * span more than one year ("Dec 30 – 31, 2026, Jan 2, 2027").
 */
const formatDateSelectionRanges = (dates: Date[], locale: string) => {
  const days = Array.from(
    new Set(dates.map((date) => moment(date).format(ISO_DATE_FORMAT))),
  )
    .sort()
    .map((date) => moment(date, ISO_DATE_FORMAT));
  const runs = groupConsecutiveDaysByMonth(days);
  const showYear = new Set(runs.map(([firstDay]) => firstDay.year())).size > 1;
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: showYear ? "numeric" : undefined,
  });

  return runs
    .map((run) =>
      1 === run.length
        ? dateFormatter.format(run[0].toDate())
        : dateFormatter.formatRange(
            run[0].toDate(),
            run[run.length - 1].toDate(),
          ),
    )
    .join(", ");
};

/**
 * Calendar props for the date picker: any number of individual days up to
 * MAX_DATE_RATE_DAYS, with days before today disabled.
 * Built per render so "today" stays current if the page is left open.
 */
const getDateRateCalendarProps = () =>
  ({
    mode: "multiple",
    max: MAX_DATE_RATE_DAYS,
    disabled: { before: new Date() },
  }) as unknown as DatePickerProps["calendarProps"];

/**
 * Returns the localized symbol for a currency code (e.g. "USD" -> "$"),
 * or an empty string when the code is missing or not a valid currency.
 */
const getCurrencySymbol = (locale: string, currency: string) => {
  if (!currency) {
    return "";
  }

  try {
    const currencyPart = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    })
      .formatToParts(0)
      .find((part) => part.type === "currency");

    return currencyPart?.value ?? "";
  } catch {
    return "";
  }
};

export {
  formatDateSelectionRanges,
  getCurrencySymbol,
  getDateRateCalendarProps,
};
